import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';

const REDIRECT_DELAY_MS = 800;

export type UnifiedRedirectSpec = {
  url: string;
  openInNewTab?: boolean;
};

/** After embedded card checkout Stripe often stays on its success UI unless we navigate (or redirect_on_completion). */
export type EmbeddedCompletionNavigate = {
  path: string;
  paymentIntentId: string;
  tenantId: string;
};

type Props = {
  stripePromise: Promise<Stripe | null> | null;
  embeddedClientSecret?: string | null;
  /** Navigate on Stripe `onComplete` using current origin (fixes API-vs-web ngrok host mismatch). */
  embeddedCompletionNavigate?: EmbeddedCompletionNavigate | null;
  redirect?: UnifiedRedirectSpec | null;
  /** Optional header row (e.g. cancel) above embedded checkout */
  embeddedHeader?: React.ReactNode;
  className?: string;
};

/**
 * Shared payment step for module pages: Stripe embedded session or provider redirect (PayPal, etc.).
 */
export function UnifiedModulePaymentCheckout({
  stripePromise,
  embeddedClientSecret,
  embeddedCompletionNavigate,
  redirect,
  embeddedHeader,
  className,
}: Props) {
  const { t } = useTranslation();
  const [redirectStarted, setRedirectStarted] = useState(false);

  const embeddedCheckoutOptions = useMemo(() => {
    if (!embeddedClientSecret) return null;
    const nav = embeddedCompletionNavigate;
    const opts: { clientSecret: string; onComplete?: () => void } = {
      clientSecret: embeddedClientSecret,
    };
    if (nav?.path && nav.paymentIntentId && nav.tenantId) {
      const path = nav.path.startsWith('/') ? nav.path : `/${nav.path}`;
      opts.onComplete = () => {
        const q = new URLSearchParams({
          tenant_id: nav.tenantId,
          web_pi: nav.paymentIntentId,
        });
        window.location.assign(`${window.location.origin}${path}?${q.toString()}`);
      };
    }
    return opts;
  }, [embeddedClientSecret, embeddedCompletionNavigate]);

  useEffect(() => {
    if (!redirect?.url || redirectStarted) return;
    setRedirectStarted(true);
    const tmr = window.setTimeout(() => {
      if (redirect.openInNewTab) {
        window.open(redirect.url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = redirect.url;
      }
    }, REDIRECT_DELAY_MS);
    return () => window.clearTimeout(tmr);
  }, [redirect?.url, redirect?.openInNewTab, redirectStarted]);

  if (embeddedClientSecret && stripePromise && embeddedCheckoutOptions) {
    return (
      <div className={className} style={className ? undefined : { marginTop: 16 }}>
        {embeddedHeader}
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={embeddedCheckoutOptions as Parameters<typeof EmbeddedCheckoutProvider>[0]['options']}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    );
  }

  if (redirect?.url) {
    return (
      <div className={className} style={{ padding: '1rem 0', textAlign: 'center' }}>
        <p className="text-muted-foreground text-sm mb-3">
          {t('payment.redirect.preparing', { defaultValue: 'Redirecting to secure payment…' })}
        </p>
        <a href={redirect.url} className="text-primary underline text-sm font-medium">
          {t('payment.redirect.continue', { defaultValue: 'Continue to payment' })}
        </a>
      </div>
    );
  }

  return null;
}
